import { screenToDoc } from "../render/Viewport";
import { createTypeLayer, isTypeLayer } from "../document/TypeLayer";
import type { TypeLayer, AntiAliasMode, WarpTextParams } from "../document/TypeLayer";
import { NewLayerCommand } from "../commands/NewLayerCommand";
import { SetLayerPropertyCommand } from "../commands/SetLayerPropertyCommand";
import type { Layer } from "../document/Layer";
import type { PointerSample, Tool, ToolContext } from "./Tool";

// Type tool -- click on the canvas to create a point text layer, or
// click-and-drag to create a paragraph text box with bounded word wrap.

/**
 * Injected by main.ts so TypeTool can measure text without touching DOM.
 * Returns the rendered width in pixels for the given text and CSS font string.
 */
export type MeasureTextFn = (text: string, fontCss: string) => number;

/**
 * Pure-JS fallback measurer -- no DOM required.
 * Approximates width as charCount * fontSize * 0.6.
 */
export function approximateMeasureText(text: string, fontCss: string): number {
  const match = fontCss.match(/(\d+(?:\.\d+)?)px/);
  const fontSize = match ? parseFloat(match[1]!) : 16;
  return text.length * fontSize * 0.6;
}

export interface TypeToolCallbacks {
  requestNewText: (x: number, y: number) => Promise<string | null>;
  requestEditText: (layer: TypeLayer, opts?: { selectAll?: boolean }) => Promise<string | null>;
  /** Immediately commit any open editor without waiting for user input. */
  commitActiveEditor?: () => void;
}

const PARAGRAPH_TEXT_MIN_DRAG = 5; // minimum drag in screen pixels to create paragraph text

/**
 * The subset of TypeLayer fields the Character/Paragraph panels can change
 * while a type layer is being edited (#i196 — restoring these on cancel).
 * `warp` stays a genuinely-optional key (omitted, never set to `undefined`)
 * to satisfy this project's exactOptionalPropertyTypes and to structurally
 * match SetLayerPropertyCommand's `Partial<Patchable>` patch type.
 */
type TypeFormattingSnapshot = {
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  fontStyle: "normal" | "italic";
  textAlign: "left" | "center" | "right" | "justify";
  textBaseline: "top" | "middle" | "bottom";
  color: { r: number; g: number; b: number; a: number };
  leading: number;
  tracking: number;
  baselineShift: number;
  underline: boolean;
  strikethrough: boolean;
  antiAlias: AntiAliasMode;
  fauxBold: boolean;
  fauxItalic: boolean;
  allCaps: boolean;
  smallCaps: boolean;
  superscript: boolean;
  subscript: boolean;
  warp?: WarpTextParams;
};

export class TypeTool implements Tool {
  readonly id = "type";
  readonly shortcut = "T";
  private editingId: string | null = null;
  private callbacks: TypeToolCallbacks | null = null;

  /** Set by main.ts to provide accurate Canvas2D text metrics. */
  measureText: MeasureTextFn | null = null;

  /**
   * Options-bar fields (#i94) — TypeToolOptions (ToolUX.ts) stashes the
   * user's chosen Font/Size/Anti-alias/Alignment/Faux Bold/Faux Italic
   * directly onto this tool instance (`this.tool.fontFamily = ...` etc., a
   * duck-typed convention shared with every other ToolOptions class). These
   * fields exist here — with the same defaults TypeToolOptions itself falls
   * back to (Arial/36/smooth/left/false/false) — so createParagraphText/
   * createNewTypeLayer below can read them back and actually apply them to
   * new type layers, instead of the layer silently reverting to
   * createTypeLayer()'s hardcoded defaults regardless of the options bar.
   */
  fontFamily = "Arial";
  fontSize = 36;
  antiAlias: AntiAliasMode = "smooth";
  textAlign: "left" | "center" | "right" | "justify" = "left";
  fauxBold = false;
  fauxItalic = false;

  // Drag state for paragraph text box creation.
  // Field named _dragActive to avoid conflicting with the Tool interface's
  // optional isDragging?(): boolean method signature.
  private dragStart: { x: number; y: number } | null = null;
  private dragCurrent: { x: number; y: number } | null = null;
  private _dragActive = false;

  setCallbacks(callbacks: TypeToolCallbacks): void {
    this.callbacks = callbacks;
  }

  /**
   * Called by EditorState.setActiveTool when the user switches away.
   * Commits any open text editing session so changes are not lost
   * (Photoshop parity: pressing V while editing commits the text).
   */
  onDeactivate(): void {
    this.callbacks?.commitActiveEditor?.();
  }

  /**
   * Open the text editor for the given layer id.  Used by the Layers panel
   * thumbnail dblclick handler to re-enter editing with all text selected
   * (Photoshop parity).
   */
  beginEditingLayerById(
    ctx: ToolContext,
    layerId: string,
    selectAll: boolean,
  ): void {
    if (!ctx.doc || !this.callbacks) return;
    const layer = this.findLayerById(ctx.doc.layers, layerId);
    if (!layer) return;
    this.editTypeLayerWithOpts(ctx, layer, { selectAll });
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);

    const clickedLayer = this.findTypeLayerAt(ctx.doc.layers, p.x, p.y);
    if (clickedLayer) {
      ctx.emit(new SetLayerPropertyCommand(clickedLayer.id, { visible: true }));
      ctx.selectLayer(clickedLayer.id);
      this.editTypeLayer(ctx, clickedLayer);
      return;
    }

    // Start tracking drag for potential paragraph text box
    this.dragStart = { x: s.x, y: s.y };
    this.dragCurrent = { x: s.x, y: s.y };
    this._dragActive = false;
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.dragStart) return;
    this.dragCurrent = { x: s.x, y: s.y };

    const dx = s.x - this.dragStart.x;
    const dy = s.y - this.dragStart.y;
    if (Math.abs(dx) > PARAGRAPH_TEXT_MIN_DRAG || Math.abs(dy) > PARAGRAPH_TEXT_MIN_DRAG) {
      this._dragActive = true;
    }

    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc || !this.dragStart) {
      this.resetDrag();
      return;
    }

    const startPos = this.dragStart;
    const endPos = { x: s.x, y: s.y };

    const dx = endPos.x - startPos.x;
    const dy = endPos.y - startPos.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > PARAGRAPH_TEXT_MIN_DRAG) {
      // Paragraph text: drag created a text box
      this.createParagraphText(ctx, startPos, endPos);
    } else {
      // Point text: simple click
      const p = screenToDoc(ctx.viewport, startPos.x, startPos.y);
      this.createNewTypeLayer(ctx, p.x, p.y);
    }

    this.resetDrag();
  }

  private resetDrag(): void {
    this.dragStart = null;
    this.dragCurrent = null;
    this._dragActive = false;
  }

  private async createParagraphText(
    ctx: ToolContext,
    start: { x: number; y: number },
    end: { x: number; y: number },
  ): Promise<void> {
    if (!this.callbacks || !ctx.doc) return;

    const p1 = screenToDoc(ctx.viewport, start.x, start.y);
    const p2 = screenToDoc(ctx.viewport, end.x, end.y);

    const x = Math.round(Math.min(p1.x, p2.x));
    const y = Math.round(Math.min(p1.y, p2.y));
    const w = Math.round(Math.abs(p2.x - p1.x));
    const h = Math.round(Math.abs(p2.y - p1.y));

    if (w < 10 || h < 10) return;

    const text = await this.callbacks.requestNewText(p1.x, p1.y);
    if (!text || !ctx.doc) return;

    const layer = createTypeLayer({
      text,
      x,
      y,
      textBounds: { x: 0, y: 0, w, h },
      name: text.slice(0, 20) || "Type Layer",
      fontFamily: this.fontFamily,
      fontSize: this.fontSize,
      antiAlias: this.antiAlias,
      textAlign: this.textAlign,
      fauxBold: this.fauxBold,
      fauxItalic: this.fauxItalic,
    });
    ctx.emit(new NewLayerCommand(layer));
  }

  private async createNewTypeLayer(ctx: ToolContext, x: number, y: number): Promise<void> {
    if (!this.callbacks) return;
    const text = await this.callbacks.requestNewText(x, y);
    if (!text || !ctx.doc) return;

    const layer = createTypeLayer({
      text,
      x: Math.round(x),
      y: Math.round(y),
      name: text.slice(0, 20) || "Type Layer",
      fontFamily: this.fontFamily,
      fontSize: this.fontSize,
      antiAlias: this.antiAlias,
      textAlign: this.textAlign,
      fauxBold: this.fauxBold,
      fauxItalic: this.fauxItalic,
    });
    ctx.emit(new NewLayerCommand(layer));
  }

  private async editTypeLayer(ctx: ToolContext, layer: TypeLayer): Promise<void> {
    return this.editTypeLayerWithOpts(ctx, layer, {});
  }

  private async editTypeLayerWithOpts(
    ctx: ToolContext,
    layer: TypeLayer,
    opts: { selectAll?: boolean },
  ): Promise<void> {
    if (!this.callbacks) return;
    this.editingId = layer.id;
    const text = await this.callbacks.requestEditText(layer, opts);
    if (text === null) {
      // Cancelled (Escape / Cancel button) — Photoshop discards the *entire*
      // editing session, not just the text. Character/Paragraph panel
      // changes made while this editor was open are applied immediately via
      // their own SetLayerPropertyCommand calls, so restore every such field
      // back to its value from when editing began, in one command (#i196).
      this.restoreFormattingOnCancel(ctx, layer);
      return;
    }
    if (text === layer.text) return;
    ctx.emit(new SetLayerPropertyCommand(layer.id, { text }));
  }

  /**
   * Fields the Character/Paragraph panels can change live while a type
   * layer is being edited. Snapshotting these on entry lets Escape/Cancel
   * revert formatting changes made mid-session, in addition to the text.
   */
  private buildFormattingSnapshot(layer: TypeLayer): TypeFormattingSnapshot {
    return {
      fontFamily: layer.fontFamily,
      fontSize: layer.fontSize,
      fontWeight: layer.fontWeight,
      fontStyle: layer.fontStyle,
      textAlign: layer.textAlign,
      textBaseline: layer.textBaseline,
      color: layer.color,
      leading: layer.leading,
      tracking: layer.tracking,
      baselineShift: layer.baselineShift,
      underline: layer.underline,
      strikethrough: layer.strikethrough,
      antiAlias: layer.antiAlias,
      fauxBold: layer.fauxBold,
      fauxItalic: layer.fauxItalic,
      allCaps: layer.allCaps,
      smallCaps: layer.smallCaps,
      superscript: layer.superscript,
      subscript: layer.subscript,
      ...(layer.warp !== undefined ? { warp: layer.warp } : {}),
    };
  }

  private restoreFormattingOnCancel(ctx: ToolContext, snapshot: TypeLayer): void {
    if (!ctx.doc) return;
    const current = this.findLayerById(ctx.doc.layers, snapshot.id);
    if (!current) return;
    const before = this.buildFormattingSnapshot(snapshot);
    const after = this.buildFormattingSnapshot(current);
    if (JSON.stringify(before) === JSON.stringify(after)) return; // nothing changed mid-session
    ctx.emit(new SetLayerPropertyCommand(snapshot.id, before));
  }

  private findTypeLayerAt(layers: readonly Layer[], x: number, y: number): TypeLayer | null {
    for (let i = layers.length - 1; i >= 0; i--) {
      const l = layers[i]!;
      if (isTypeLayer(l)) {
        const bounds = this.getTypeLayerBounds(l);
        if (bounds && x >= bounds.x && x <= bounds.x + bounds.w && y >= bounds.y && y <= bounds.y + bounds.h) {
          return l;
        }
      }
      if (l.kind === "group") {
        const found = this.findTypeLayerAt(l.children, x, y);
        if (found) return found;
      }
    }
    return null;
  }

  private findLayerById(layers: readonly Layer[], id: string): TypeLayer | null {
    for (const l of layers) {
      if (isTypeLayer(l) && l.id === id) return l;
      if (l.kind === "group") {
        const found = this.findLayerById(l.children, id);
        if (found) return found;
      }
    }
    return null;
  }

  /** Compute approximate text bounds using injected or approximate text metrics. */
  private getTypeLayerBounds(layer: TypeLayer): { x: number; y: number; w: number; h: number } | null {
    // For paragraph text, use the textBounds directly
    if (layer.textBounds) {
      return {
        x: layer.x + layer.textBounds.x,
        y: layer.y + layer.textBounds.y,
        w: layer.textBounds.w,
        h: layer.textBounds.h,
      };
    }

    if (layer.text.length === 0) return null;

    let weight = layer.fontWeight;
    if (layer.fauxBold) weight = Math.min(900, weight + 300);
    let style = layer.fontStyle;
    if (layer.fauxItalic && style === "normal") style = "italic";
    const fontSize = layer.fontSize * (layer.superscript ? 0.6 : layer.subscript ? 0.6 : 1);
    const fontCss = `${style} ${weight} ${fontSize}px "${layer.fontFamily}"`;
    const measure = this.measureText ?? approximateMeasureText;

    let displayText = layer.text;
    if (layer.allCaps) displayText = displayText.toUpperCase();

    const lines = displayText.split("\n");
    const lineHeight = layer.fontSize * layer.leading;
    let maxWidth = 0;
    for (const line of lines) {
      const w = measure(line, fontCss) + Math.abs(layer.tracking) * 0.01 * fontSize * line.length;
      if (w > maxWidth) maxWidth = w;
    }

    const totalHeight = lines.length * lineHeight;

    // Account for text alignment
    let x = layer.x;
    if (layer.textAlign === "center") x -= maxWidth / 2;
    else if (layer.textAlign === "right") x -= maxWidth;

    // Account for baseline
    let y = layer.y;
    if (layer.textBaseline === "middle") y -= totalHeight / 2;
    else if (layer.textBaseline === "bottom") y -= totalHeight;

    // Add padding for underline/strikethrough
    const padding = 4;
    return {
      x: x - padding,
      y: y - padding,
      w: maxWidth + padding * 2,
      h: totalHeight + padding * 2,
    };
  }

  /** Get the current drag state for rendering the paragraph text box preview. */
  getDragPreview(): { x: number; y: number; w: number; h: number } | null {
    if (!this.dragStart || !this.dragCurrent || !this._dragActive) return null;
    const x = Math.min(this.dragStart.x, this.dragCurrent.x);
    const y = Math.min(this.dragStart.y, this.dragCurrent.y);
    const w = Math.abs(this.dragCurrent.x - this.dragStart.x);
    const h = Math.abs(this.dragCurrent.y - this.dragStart.y);
    return { x, y, w, h };
  }
}
